import React from 'react';
import { EnvelopeSimple, MinusCircle, Phone, PlusCircle, Star, type Icon } from '@phosphor-icons/react';
import type { KontaktKanal, Kanal } from '../../lib/kontakt/kontaktKanaele';
import { useViewport } from '../island/hooks';
import { ABSCHNITT_FARBE, FORM, GLANZ, INNEN } from '../island/form/form-stil';

interface KontaktKanaeleEditorProps {
  kanaele: KontaktKanal[];
  onChange: (kanaele: KontaktKanal[]) => void;
}

// Optik der Pillen-Formulare (Handoff Pillen-Formulare, Abschnitt „Kontaktwege“): je Kanal eine dunkle
// Gruppe mit Icon-Kachel, Zeilen mit Stern (Haupt-Kontakt), Wert, Label und Entfernen.
const KontaktKanaeleEditor: React.FC<KontaktKanaeleEditorProps> = ({ kanaele, onChange }) => {
  const { width } = useViewport();
  const update = (index: number, patch: Partial<KontaktKanal>) => {
    onChange(kanaele.map((k, i) => (i === index ? { ...k, ...patch } : k)));
  };
  const entfernen = (index: number) => onChange(kanaele.filter((_, i) => i !== index));
  const hinzufuegen = (kanal: Kanal) =>
    onChange([...kanaele, { kanal, wert: '', label: null, ist_primaer: false, sortierung: 0, id: crypto.randomUUID() }]);
  const setzePrimaer = (index: number) => {
    const ziel = kanaele[index];
    onChange(kanaele.map((k, i) => (k.kanal === ziel.kanal ? { ...k, ist_primaer: i === index } : k)));
  };

  const renderGruppe = (kanal: Kanal, titel: string, I: Icon, verlauf: string, placeholder: string) => {
    const eintraege = kanaele.map((k, i) => ({ k, i })).filter((x) => x.k.kanal === kanal);
    return (
      <div className="relative flex flex-col overflow-hidden" style={{
        borderRadius: 16, background: INNEN.flaeche,
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.35),inset 0 0 0 0.5px rgba(255,255,255,0.06),0 1px 0 rgba(255,255,255,0.05)',
      }}>
        <div className="flex items-center" style={{ gap: 9, padding: '10px 12px 8px' }}>
          <span aria-hidden className="relative flex items-center justify-center overflow-hidden" style={{
            width: 28, height: 28, borderRadius: 9, background: verlauf, boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.45),0 4px 10px -4px rgba(0,0,0,0.6)',
          }}>
            <span className="absolute inset-0" style={{ background: GLANZ }} />
            <I size={14} weight="fill" color="#fff" className="relative" />
          </span>
          <span className="flex-1" style={{ fontSize: 13, fontWeight: 800 }}>{titel}</span>
          {eintraege.length > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{eintraege.length}</span>}
        </div>
        {eintraege.map(({ k, i }) => (
          <div key={k.id ?? `${kanal}-${i}`} className="pf-drop flex items-center" style={{ gap: 6, padding: '6px 8px 6px 6px', borderTop: `0.5px solid ${INNEN.linie}` }}>
            <button type="button" onClick={() => setzePrimaer(i)} data-tip="Als Haupt markieren" aria-label="Als Haupt-Kontakt markieren" aria-pressed={k.ist_primaer}
              className="flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent" style={{ width: 30, height: 30, borderRadius: 9 }}>
              <Star size={15} weight={k.ist_primaer ? 'fill' : 'bold'} color={k.ist_primaer ? FORM.gold : 'rgba(255,255,255,0.3)'} />
            </button>
            <input type={kanal === 'email' ? 'email' : 'tel'} value={k.wert} placeholder={placeholder} aria-label={titel}
              onChange={(e) => update(i, { wert: e.target.value })}
              className="pf-klein min-w-0 flex-1 border-none bg-transparent outline-none"
              style={{ font: 'inherit', fontSize: 14, fontWeight: 600, color: FORM.text }} />
            <input type="text" value={k.label || ''} placeholder="Label" aria-label="Label" data-tip="z. B. privat, Arbeit, Notfall"
              onChange={(e) => update(i, { label: e.target.value || null })}
              className="pf-klein shrink-0 border-none outline-none"
              style={{ width: 74, height: 26, borderRadius: 8, padding: '0 8px', font: 'inherit', fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.08)' }} />
            <button type="button" onClick={() => entfernen(i)} aria-label="Entfernen" data-tip="Entfernen"
              className="flex shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent" style={{ width: 28, height: 28, color: 'rgba(255,123,114,0.75)' }}>
              <MinusCircle size={16} weight="bold" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => hinzufuegen(kanal)} data-tip="Weitere Adresse oder Nummer anlegen – der Stern markiert den Haupt-Kontakt"
          className="pf-zeile mt-auto flex cursor-pointer items-center border-none bg-transparent text-left"
          style={{ gap: 8, padding: '10px 12px', borderTop: `0.5px solid ${INNEN.linie}`, font: 'inherit', fontSize: 13, fontWeight: 700, color: FORM.gold }}>
          <PlusCircle size={17} weight="fill" /> {titel} hinzufügen
        </button>
      </div>
    );
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: width < 600 ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap: 10 }}>
      {renderGruppe('email', 'E-Mails', EnvelopeSimple, ABSCHNITT_FARBE.blau.verlauf, 'name@example.de')}
      {renderGruppe('telefon', 'Telefon', Phone, ABSCHNITT_FARBE.gruen.verlauf, '0641 1234567')}
    </div>
  );
};

export default KontaktKanaeleEditor;
